import React from 'react';
import styled from 'styled-components';
import { ReactComponent as AthenaIcon } from '@elementary/common-ui/assets/data-warehouses/athena.svg';
import { ReactComponent as BigQueryIcon } from '@elementary/common-ui/assets/data-warehouses/bigquery.svg';
import { ReactComponent as ClickhouseIcon } from '@elementary/common-ui/assets/data-warehouses/clickhouse.svg';
import { ReactComponent as DatabricksIcon } from '@elementary/common-ui/assets/data-warehouses/databricks.svg';
import { ReactComponent as PostgresIcon } from '@elementary/common-ui/assets/data-warehouses/postgres.svg';
import { ReactComponent as RedshiftIcon } from '@elementary/common-ui/assets/data-warehouses/redshift.svg';
import { ReactComponent as SnowflakeIcon } from '@elementary/common-ui/assets/data-warehouses/snowflake.svg';
import { ReactComponent as DremioIcon } from '@elementary/common-ui/assets/data-warehouses/dremio.svg';
import { ReactComponent as FabricIcon } from '@elementary/common-ui/assets/data-warehouses/fabric.svg';
import {
  TextField,
  FormField,
  formFieldRequiredLabel as requiredLabel,
  FlexColumn,
  SegmentedButtonGroup,
  SegmentedButton,
  DropDown,
} from '@elementary/common-ui/components';
import {
  SectionTitle,
  InfoBox,
} from '@elementary/common-ui/components/FormControls';
import { Divider } from '@mui/material';
import { Controller, useFormContext } from 'react-hook-form';
import { WarehouseType } from '@elementary/common-ui/types';
import { space } from '@elementary/common-ui/style/utils';
import { ELEMENTARY_IP, ENVIRONMENT_TYPE_OPTIONS } from './constants';
import SnowflakeFormContent from './SnowflakeFormContent';
import BigqueryFormContent from './BigqueryFormContent';
import RedshiftFormContent from './RedshiftFormContent';
import DatabricksFormContent from './DatabricksFormContent';
import PostgresFormContent from './PostgresFormContent';
import AthenaFormContent from './AthenaFormContent';
import ClickhouseFormContent from './ClickhouseFormContent';
import DremioFormContent from './DremioFormContent';
import FabricFormContent from './FabricFormContent';

type Props = {
  disabled?: boolean;
};

const Wrapper = styled(FlexColumn)`
  gap: ${space('xxl')};
`;

enum DataWarehouseTypes {
  Snowflake = 'snowflake',
  Redshift = 'redshift',
  BigQuery = 'bigquery',
  Databricks = 'databricks',
  Postgres = 'postgres',
  Athena = 'athena',
  Clickhouse = 'clickhouse',
  Dremio = 'dremio',
  Fabric = 'fabric',
}

const warehouseOptions = [
  {
    value: DataWarehouseTypes.Snowflake,
    tooltip: 'Snowflake',
    icon: <SnowflakeIcon width={36} height={36} />,
  },
  {
    value: DataWarehouseTypes.Redshift,
    tooltip: 'Redshift',
    icon: <RedshiftIcon width={36} height={36} />,
  },
  {
    value: DataWarehouseTypes.BigQuery,
    tooltip: 'BigQuery',
    icon: <BigQueryIcon width={36} height={36} />,
  },
  {
    value: DataWarehouseTypes.Databricks,
    tooltip: 'Databricks',
    icon: <DatabricksIcon width={36} height={36} />,
  },
  {
    value: DataWarehouseTypes.Postgres,
    tooltip: 'Postgres',
    icon: <PostgresIcon width={36} height={36} />,
  },
  {
    value: DataWarehouseTypes.Athena,
    tooltip: 'Athena',
    icon: <AthenaIcon width={36} height={36} />,
  },
  {
    value: DataWarehouseTypes.Dremio,
    tooltip: 'Dremio',
    icon: <DremioIcon width={36} height={36} />,
  },
  {
    value: DataWarehouseTypes.Clickhouse,
    tooltip: 'ClickHouse',
    icon: <ClickhouseIcon width={36} height={36} />,
  },
  {
    value: DataWarehouseTypes.Fabric,
    tooltip: 'Microsoft Fabric',
    icon: <FabricIcon width={36} height={36} />,
  },
];

const WarehouseFormContent = ({
  warehouseType,
  disabled,
}: {
  warehouseType: WarehouseType;
  disabled?: boolean;
}) => {
  switch (warehouseType) {
    case 'snowflake':
      return <SnowflakeFormContent disabled={disabled} />;
    case 'bigquery':
      return <BigqueryFormContent disabled={disabled} />;
    case 'redshift':
      return <RedshiftFormContent disabled={disabled} />;
    case 'databricks':
      return <DatabricksFormContent disabled={disabled} />;
    case 'postgres':
      return <PostgresFormContent disabled={disabled} />;
    case 'athena':
      return <AthenaFormContent disabled={disabled} />;
    case 'clickhouse':
      return <ClickhouseFormContent disabled={disabled} />;
    case 'dremio':
      return <DremioFormContent disabled={disabled} />;
    case 'fabric':
      return <FabricFormContent disabled={disabled} />;
    default:
      return null;
  }
};

const EnvironmentSetupStep = ({ disabled }: Props) => {
  const methods = useFormContext();
  const { control, register, watch, formState } = methods;

  const warehouseType = watch('warehouseType') as WarehouseType;

  return (
    <Wrapper>
      <SectionTitle>Environment details</SectionTitle>

      <FormField
        title="Environment name"
        errorMessage={formState.errors.environmentName?.message as string}
      >
        <TextField
          {...register('environmentName', {
            required: requiredLabel,
          })}
          variant="outlined"
          placeholder="e.g. Production"
          disabled={disabled}
          size="large"
        />
      </FormField>

      <FormField title="Environment type" optional>
        <Controller
          control={control}
          name="environmentType"
          render={({ field }) => (
            <DropDown
              {...field}
              options={ENVIRONMENT_TYPE_OPTIONS}
              placeholder="Select environment type"
              disabled={disabled}
              showSearch={false}
              showClear={false}
              size="large"
            />
          )}
        />
      </FormField>

      <Divider sx={{ mx: '-24px', pt: '8px' }} />
      <SectionTitle>Select source details</SectionTitle>

      <InfoBox>
        If you use an allowlist to manage DWH access, add the Elementary IP:{' '}
        {ELEMENTARY_IP}.
      </InfoBox>

      <FormField
        title="Select your data warehouse"
        errorMessage={formState.errors.warehouseType?.message as string}
      >
        <Controller
          control={methods.control}
          name="warehouseType"
          rules={{ required: requiredLabel }}
          render={({ field }) => (
            <SegmentedButtonGroup
              {...field}
              onChange={(_, value) => {
                if (value != null) {
                  methods.setValue('warehouseType', value, {
                    shouldValidate: true,
                  });
                }
              }}
              exclusive
            >
              {warehouseOptions.map((warehouseOption) => (
                <SegmentedButton
                  key={warehouseOption.value}
                  title={warehouseOption.tooltip}
                  value={warehouseOption.value}
                  size="large"
                >
                  {warehouseOption.icon}
                </SegmentedButton>
              ))}
            </SegmentedButtonGroup>
          )}
        />
      </FormField>

      {warehouseType && (
        <WarehouseFormContent
          warehouseType={warehouseType}
          disabled={disabled}
        />
      )}
    </Wrapper>
  );
};

export default EnvironmentSetupStep;
